import { Collapse } from "@blueprintjs/core";
import classnames from "classnames";
import { Caret } from "modules/common/components/collapse/Caret";
import { SORTABLE_DATE_FORMAT } from "modules/common/constants";
import { columnLeft, columnsWidth, PlanGrid } from "modules/spending/workload-plan/models/plan-scale";
import { SECTION_PADDING } from "modules/spending/workload-plan/components/plan-geometry";
import moment from "moment";
import React, { useCallback } from "react";
import { ProjectPlanProject } from "../models/project-plan-types";
import { PlanTrack, trackStyles } from "./PlanTrack";
import { ProjectPlanRowView } from "./ProjectPlanRowView";
import styles from "./ProjectSection.module.scss";

/**
 * Ширина колонки проектов. Шире табельных 240px: здесь две строки — название
 * с номером и «до … · ГИП …». Обязана совпадать с --wp-name-width в
 * ProjectPlanPage.module.scss, иначе шапка сетки разъедется со строками.
 */
const PROJECT_NAME_WIDTH = 300;


interface ProjectSectionProps {
    project: ProjectPlanProject;
    grid: PlanGrid;
    monthBreaks: number[];
    selectedSlot: { start: number; length: number } | null;
    collapsed: boolean;
    onToggle: (projectId: string) => void;
}

/**
 * Проект в списке — строка обычной таблицы, которая раскрывается в графики участников.
 * В свёрнутом виде показывает сводную полосу и крайний день работ: ради этого экран и нужен.
 */
export const ProjectSection: React.FC<ProjectSectionProps> = React.memo((props) => {
    const { project, grid, monthBreaks, selectedSlot, collapsed, onToggle } = props;

    const toggle = useCallback(() => onToggle(project.id), [onToggle, project.id]);

    // Свёрнутый проект не рендерит строк, и max-content схлопнул бы блок до ширины
    // названия. Держим ширину сетки явно — строка проекта идёт во всю таблицу.
    // Отступ считается один раз: он только слева, у .header и .rows.
    const width = PROJECT_NAME_WIDTH + grid.totalWidth + SECTION_PADDING;

    const lastDay = project.lastDay
        ? moment(project.lastDay, SORTABLE_DATE_FORMAT).format("DD.MM.YYYY")
        : "";

    // Вторая строка колонки: срок и ГИП. Срок первым — ради него экран и открывают,
    // а имя при нехватке места уйдёт в многоточие, не спрятав дату.
    const subtitle = [lastDay && `до ${lastDay}`, project.employer]
        .filter(Boolean)
        .join(" · ");

    const summaryLeft = project.summary ? columnLeft(grid, project.summary.start) : 0;
    const summaryWidth = project.summary
        ? columnsWidth(grid, project.summary.start, project.summary.length)
        : 0;

    return (
        // Цвет проекта уходит вниз одной CSS-переменной: им подсвечиваются левая
        // полоса, нижняя граница заголовка и нижняя граница последней строки —
        // раскрытый проект читается как один блок своего цвета.
        <div
            className={styles.block}
            style={{ minWidth: width, "--project-color": project.paint.chipBackground } as React.CSSProperties}
        >
            <div
                className={classnames(styles.header, { [styles.expanded]: !collapsed })}
                onClick={toggle}
            >
                {/* подсказка своя, а не нативный title: тот держит паузу около секунды */}
                <div
                    className={styles.name}
                    data-tip={`${project.fullInventoryNumber} ${project.name}`}
                    data-tip-note={[
                        lastDay && `Крайний день работ — ${lastDay}`,
                        project.employer && `ГИП ${project.employer}`,
                    ]
                        .filter(Boolean)
                        .join(", ")}
                >
                    <Caret collapsed={collapsed} />

                    {/*
                        Крайний день стоит в колонке названия, а не у полосы: колонка
                        липкая, поэтому дата видна всегда. У полосы она пряталась за
                        горизонтальной прокруткой ровно на тех проектах, где нужнее всего —
                        на длинных, уезжающих вправо.
                    */}
                    <span
                        className={classnames(styles.inventory, {
                            [styles.inventoryDark]: project.paint.chipDark,
                            [styles.inventoryLight]: !project.paint.chipDark,
                        })}
                        style={{ backgroundColor: project.paint.chipBackground }}
                    >
                        {project.fullInventoryNumber}
                    </span>

                    <div className={styles.info}>
                        <div className={styles.title}>{project.name}</div>

                        {subtitle && <div className={styles.lastDay}>{subtitle}</div>}
                    </div>
                </div>

                <PlanTrack
                    grid={grid}
                    monthBreaks={monthBreaks}
                    selectedSlot={selectedSlot}
                    className={styles.track}
                >
                    {project.summary && (
                        <div
                            className={classnames(trackStyles.block, styles.summary, {
                                [trackStyles.blockDark]: project.paint.dark,
                                [trackStyles.blockLight]: !project.paint.dark,
                                [trackStyles.cutLeft]: project.summary.continuesLeft,
                                [trackStyles.cutRight]: project.summary.continuesRight,
                            })}
                            style={{
                                left: summaryLeft,
                                width: summaryWidth,
                                backgroundColor: project.paint.background,
                                borderColor: project.paint.borderColor,
                            }}
                            data-tip={`${project.fullInventoryNumber} ${project.name}`}
                            data-tip-note={lastDay ? `Крайний день работ — ${lastDay}` : ""}
                        />
                    )}

                </PlanTrack>
            </div>

            {/* Раскрытие — тот же Collapse с внутренним блоком, что у отделов табеля-плана */}
            <Collapse isOpen={!collapsed} keepChildrenMounted={false}>
                <div className={styles.rows}>
                    {project.rows.map((row) => (
                        <ProjectPlanRowView
                            key={`${row.kind}_${row.id}`}
                            row={row}
                            paint={project.paint}
                            label={project.fullInventoryNumber}
                            grid={grid}
                            monthBreaks={monthBreaks}
                            selectedSlot={selectedSlot}
                        />
                    ))}
                </div>
            </Collapse>
        </div>
    );
});

ProjectSection.displayName = "ProjectSection";

